/**
 * Redux slice for measurement tools state management.
 *
 * Manages active measurement tool, collected points, units, and coordinate display.
 *
 * Validates: Requirements 19.1, 19.2, 19.5, 20.1, 20.3, 20.5, 21.1, 21.2, 21.3
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DistanceUnits, AreaUnits, CoordinateFormat } from '../../services/MeasurementService';

export type MeasurementMode = 'distance' | 'area' | 'coordinate' | null;

export interface MeasurementState {
  /** Currently active measurement tool */
  activeMode: MeasurementMode;
  /** Points collected for distance measurement [lon, lat][] */
  distancePoints: [number, number][];
  /** Points collected for area measurement [lon, lat][] */
  areaPoints: [number, number][];
  /** Current distance unit */
  distanceUnits: DistanceUnits;
  /** Current area unit */
  areaUnits: AreaUnits;
  /** Current coordinate format */
  coordinateFormat: CoordinateFormat;
  /** Live cursor coordinates [lon, lat] */
  cursorCoordinates: [number, number] | null;
  /** Clicked/persisted coordinate [lon, lat] */
  clickedCoordinate: [number, number] | null;
  /** Whether measurement is in progress (user is adding points) */
  isMeasuring: boolean;
  /** Current cumulative distance result */
  currentDistance: number;
  /** Current area result */
  currentArea: number;
  /** Current perimeter result */
  currentPerimeter: number;
}

const initialState: MeasurementState = {
  activeMode: null,
  distancePoints: [],
  areaPoints: [],
  distanceUnits: 'meters',
  areaUnits: 'square-meters',
  coordinateFormat: 'decimal-degrees',
  cursorCoordinates: null,
  clickedCoordinate: null,
  isMeasuring: false,
  currentDistance: 0,
  currentArea: 0,
  currentPerimeter: 0,
};

const measurementSlice = createSlice({
  name: 'measurement',
  initialState,
  reducers: {
    setMeasurementMode: (state, action: PayloadAction<MeasurementMode>) => {
      // Reset points when switching modes
      state.activeMode = action.payload;
      state.distancePoints = [];
      state.areaPoints = [];
      state.isMeasuring = false;
      state.currentDistance = 0;
      state.currentArea = 0;
      state.currentPerimeter = 0;
      state.clickedCoordinate = null;
    },

    addDistancePoint: (state, action: PayloadAction<[number, number]>) => {
      state.distancePoints.push(action.payload);
      state.isMeasuring = true;
    },

    addAreaPoint: (state, action: PayloadAction<[number, number]>) => {
      state.areaPoints.push(action.payload);
      state.isMeasuring = true;
    },

    setDistanceUnits: (state, action: PayloadAction<DistanceUnits>) => {
      state.distanceUnits = action.payload;
    },

    setAreaUnits: (state, action: PayloadAction<AreaUnits>) => {
      state.areaUnits = action.payload;
    },

    setCoordinateFormat: (state, action: PayloadAction<CoordinateFormat>) => {
      state.coordinateFormat = action.payload;
    },

    setCursorCoordinates: (state, action: PayloadAction<[number, number] | null>) => {
      state.cursorCoordinates = action.payload;
    },

    setClickedCoordinate: (state, action: PayloadAction<[number, number] | null>) => {
      state.clickedCoordinate = action.payload;
    },

    updateCurrentDistance: (state, action: PayloadAction<number>) => {
      state.currentDistance = action.payload;
    },

    updateCurrentArea: (state, action: PayloadAction<number>) => {
      state.currentArea = action.payload;
    },

    updateCurrentPerimeter: (state, action: PayloadAction<number>) => {
      state.currentPerimeter = action.payload;
    },

    finishMeasurement: (state) => {
      state.isMeasuring = false;
    },

    clearMeasurement: (state) => {
      state.distancePoints = [];
      state.areaPoints = [];
      state.isMeasuring = false;
      state.currentDistance = 0;
      state.currentArea = 0;
      state.currentPerimeter = 0;
    },

    resetMeasurement: () => initialState,
  },
});

export const {
  setMeasurementMode,
  addDistancePoint,
  addAreaPoint,
  setDistanceUnits,
  setAreaUnits,
  setCoordinateFormat,
  setCursorCoordinates,
  setClickedCoordinate,
  updateCurrentDistance,
  updateCurrentArea,
  updateCurrentPerimeter,
  finishMeasurement,
  clearMeasurement,
  resetMeasurement,
} = measurementSlice.actions;

export default measurementSlice.reducer;
